import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, ImageIcon, Paperclip, Video, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { appDialogSecondaryButton } from "@/components/ui/app-dialog";
import { CameraModal } from "@/components/CameraModal";
import { VideoRecorderModal } from "./VideoRecorderModal";

/** Limite por anexo, igual ao da API (nginx/PHP aceitam até 60 MB). */
export const ENDORSEMENT_MAX_FILE_MB = 60;
export const ENDORSEMENT_MAX_FILES = 10;
const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm";

type FilePickerProps = {
  files: File[];
  onChange: (files: File[]) => void;
  /** Quantos anexos já existem na solicitação (conta no limite). */
  existingCount?: number;
  disabled?: boolean;
};

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

/** Celular/tablet (tela de toque): usa a câmera nativa do aparelho pelo input com capture. */
const isTouchDevice = () =>
  typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

/**
 * Seleção de foto/vídeo.
 * - Celular: "Tirar foto ou gravar vídeo" abre a câmera do aparelho (atributo capture).
 * - Computador (LOT-68): "Tirar foto" e "Gravar vídeo" usam a webcam dentro do sistema.
 * Em ambos, "Escolher da galeria/arquivo" abre o seletor de arquivos.
 */
export const FilePicker = ({ files, onChange, existingCount = 0, disabled }: FilePickerProps) => {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [isTouch] = useState(isTouchDevice);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const previews = useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files]
  );

  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const handleSelected = (selected: File[]) => {
    const accepted: File[] = [];

    for (const file of selected) {
      if (file.size > ENDORSEMENT_MAX_FILE_MB * 1024 * 1024) {
        toast.error(`"${file.name}" passa de ${ENDORSEMENT_MAX_FILE_MB} MB (${formatSize(file.size)}).`);
        continue;
      }
      accepted.push(file);
    }

    const room = ENDORSEMENT_MAX_FILES - existingCount - files.length;
    if (accepted.length > room) {
      toast.error(`Limite de ${ENDORSEMENT_MAX_FILES} anexos por solicitação.`);
    }

    onChange([...files, ...accepted.slice(0, Math.max(room, 0))]);
  };

  const bindInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSelected(Array.from(e.target.files || []));
    e.target.value = "";
  };

  return (
    <div className="space-y-2">
      <input
        ref={cameraRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        className="hidden"
        onChange={bindInput}
      />
      <input
        ref={galleryRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={bindInput}
      />

      {isTouch ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button
            type="button"
            disabled={disabled}
            className={appDialogSecondaryButton}
            onClick={() => cameraRef.current?.click()}
          >
            <Camera className="mr-2 h-4 w-4 text-primary" />
            Tirar foto ou gravar vídeo
          </Button>
          <Button
            type="button"
            disabled={disabled}
            className={appDialogSecondaryButton}
            onClick={() => galleryRef.current?.click()}
          >
            <Paperclip className="mr-2 h-4 w-4 text-primary" />
            Escolher da galeria
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Button
            type="button"
            disabled={disabled}
            className={appDialogSecondaryButton}
            onClick={() => setPhotoOpen(true)}
          >
            <Camera className="mr-2 h-4 w-4 text-primary" />
            Tirar foto
          </Button>
          <Button
            type="button"
            disabled={disabled}
            className={appDialogSecondaryButton}
            onClick={() => setVideoOpen(true)}
          >
            <Video className="mr-2 h-4 w-4 text-primary" />
            Gravar vídeo
          </Button>
          <Button
            type="button"
            disabled={disabled}
            className={appDialogSecondaryButton}
            onClick={() => galleryRef.current?.click()}
          >
            <Paperclip className="mr-2 h-4 w-4 text-primary" />
            Escolher arquivo
          </Button>
        </div>
      )}
      <p className="text-xs text-gray-500">
        Foto (JPG, PNG, WEBP, HEIC) ou vídeo (MP4, MOV, WEBM), até {ENDORSEMENT_MAX_FILE_MB} MB cada.
      </p>

      {previews.length > 0 && (
        <ul className="space-y-2">
          {previews.map(({ file, url }, index) => {
            const isVideo = file.type.startsWith("video/");

            return (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-3 rounded-lg border bg-white p-2 dark:bg-zinc-900"
              >
                {isVideo ? (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-blue-50 text-blue-600">
                    <Video className="h-5 w-5" />
                  </div>
                ) : (
                  <img src={url} alt={file.name} className="h-10 w-10 shrink-0 rounded border object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="flex items-center gap-1 text-xs text-gray-500">
                    {isVideo ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                    {formatSize(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={disabled}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-zinc-800"
                  onClick={() => onChange(files.filter((_, i) => i !== index))}
                  title="Remover arquivo"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <CameraModal
        isOpen={photoOpen}
        onClose={() => setPhotoOpen(false)}
        onCapture={(file) => handleSelected([file])}
      />
      <VideoRecorderModal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        onCapture={(file) => handleSelected([file])}
        maxSizeMb={ENDORSEMENT_MAX_FILE_MB}
      />
    </div>
  );
};
